<script setup lang="ts">
import { useRoute } from 'vue-router'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useNuxtApp } from '#app'
import { getGoodDetail } from '@/api'
import config from '~/config/index'
import { nextLevelCoupon as queryNextLevelCoupon } from '@/api/mall'
import { ElLoading, ElRate, ElButton, ElMessage, ElDialog } from 'element-plus'
import MistrivoProductInformation from '@/templates/australia/components/MistrivoProductInformation.vue'
import MistrivoProductGallery from '@/templates/australia/components/MistrivoProductGallery.vue'
import MistrivoPaymentMarks from '@/templates/australia/components/MistrivoPaymentMarks.vue'
import MistrivoRecommendations from '@/templates/australia/components/MistrivoRecommendations.vue'
import MistrivoServiceBenefits from '@/templates/australia/components/MistrivoServiceBenefits.vue'
import RateItem from '../mall/product/RateItem.vue'
import { useWindowResize } from '~/composables/useWindowResize'
import { trackEvent } from '~/utils/analytics'
import { $t,transferOssUrl } from '~/utils/index'
import { useCdnDomain } from '~/composables/useCdnDomain'
import { useAustraliaDisplayCurrency } from '~/templates/australia/composables/useAustraliaDisplayCurrency'
import { getSkuOptionDiscountDisplay } from '@/templates/australia/utils/discountDisplay'
import { getAustraliaSkuDiscountUnitPrice } from '@/templates/australia/utils/discountUnitPrice'
import { getAustraliaProductImages } from '@/templates/australia/utils/productImages'
import { normalizeAustraliaPurchaseDetail } from '@/templates/australia/utils/purchase'
import MistrivoSpuPurchaseOptions from '@/templates/australia/components/MistrivoSpuPurchaseOptions.vue'
// 使用窗口大小监听 - 只用于小屏幕检测，避免样式冲突
const { windowWidth } = useWindowResize()
const { $customFetch } = useNuxtApp()
const { formatMoney, baseCurrencyCode } = useAustraliaDisplayCurrency()

// 使用 CDN 域名替换
const { replaceCdnDomain } = useCdnDomain()

// 小屏幕检测 - 480px以下
const isSmallScreen = computed(() => process.client ? windowWidth.value <= 480 : false)

const route = useRoute()
// SSR-provided product detail (optional)
const props = defineProps<{ 
  ssrProductDetail?: any
  isModal?: boolean // 是否在弹窗模式下显示
  initialComboCode?: string
}>()

// 定义 emit 事件
const emit = defineEmits(['addToCartSuccess'])
const buyTotalQuantity = ref(1)
const reviewSection = ref<HTMLElement | null>(null)
function scrollToReviews() { reviewSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }
const slideIndex = ref(0)
const thumbsRef = ref<HTMLElement | null>(null)
const productCardRef = ref<HTMLElement | null>(null)
const productHeadingRef = ref<HTMLElement | null>(null)
const ratingValue = ref<number | undefined>(undefined)
const reviewsRef = ref<any>(null)
const commentNum = ref(0)
const totalReviews = ref(0)
const averageRating = ref(0)
const activeTab = ref<'detail' | 'reviews'>('detail')
const detailId = computed(() => String(route.query?.spuId || route.query?.detailId || ''))
const productInfo = ref<any>({})
const purchasePrice = ref<any>(null)
const productError = ref(false)
async function getDetail(keepSelection = false) {
  const previousSkuId = keepSelection ? selectedSku.value?.id : null
  const loadingInstance = ElLoading.service({
    lock: true,
    text: 'Loading',
    background: 'rgba(255, 255, 255, 0.5)',
  })

  try {
    const res = await getGoodDetail({
      param: {
        id: String(productInfo.value.id || detailId.value || ''),
        comboCode: props.initialComboCode ?? route.query?.comboCode ?? undefined,
        dealerShopId: String(productInfo.value.dealerShopId || route.query?.dealerShopId || ''),
      }
    })
    const skuIconUrl = ref("");
    // 处理新的数据结构
    if (res.data.dealerItemSkuDTOList) {
      res.data.dealerItemSkuDTOList = res.data.dealerItemSkuDTOList.map(
        (o: any) => {
          if (o.iconUrl) {
            skuIconUrl.value = o.iconUrl;
          }
          if (o.state == 0) {
            o.inventory = 0;
          }
          return o;
        }
      );
    }
    productInfo.value = normalizeAustraliaPurchaseDetail(res.data)

    // 初始化规格选择
    initializeSpecSelection()
    if (previousSkuId) {
      const previous = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => sku.id === previousSkuId)
      if (previous) applySkuSelection(previous)
    }

    productError.value = false
  }
  catch (error) {
    if (!keepSelection) productError.value = true
  }
  finally {
    loadingInstance.close()
  }
}




const selectedAttributes = ref<any>({})
const selectedSku = ref<any>(null)

function handleSelectSpecValue(spec: any, attrValue: any) {
  selectedAttributes.value = {
    ...selectedAttributes.value,
    [spec.id]: attrValue.id,
  }

  // 根据选中的规格匹配SKU
  matchSelectedSku()
  if (!selectedSku.value) {
    const fallback = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => sku.state !== 0 && Number(sku.inventory) > 0 && sku.spItemSkuSpecificationIds?.split(',').includes(`${spec.id}-${attrValue.id}`))
    if (fallback) applySkuSelection(fallback)
  }

  // 更新当前显示的图片
  updateCurrentImage(attrValue.skuImage)

}

function attrValueIsSelected(specId: string, attrValueId: string) {
  return selectedAttributes.value[specId] === attrValueId
}

function getSpecOptionPrice(specId: string, valueId: string) {
  const candidates = (productInfo.value.dealerItemSkuDTOList || []).filter((sku: any) => {
    const ids = String(sku.spItemSkuSpecificationIds || '').split(',')
    return ids.includes(`${specId}-${valueId}`) && Object.entries(selectedAttributes.value).every(([key, value]) => String(key) === String(specId) || ids.includes(`${key}-${value}`))
  })
  const prices = candidates.map((sku: any) => {
    const base = Number(sku.finalPrice ?? sku.price ?? sku.skuPrice)
    const member = Number(sku.discountPrice || productInfo.value.discountPrice || 0)
    return member && base && member !== base ? member : base
  }).filter((price: number) => Number.isFinite(price) && price >= 0)
  if (!prices.length) return ''
  return `${new Set(prices).size > 1 ? 'From ' : ''}${formatAustraliaDisplayMoney(Math.min(...prices))}`
}

function getSpecOptionDiscount(specId: string, valueId: string) {
  return getSkuOptionDiscountDisplay(productInfo.value.dealerItemSkuDTOList, selectedAttributes.value, specId, valueId)
}

// 检查某个规格值是否有可用库存
function isSpecValueAvailable(specId: string, attrValueId: string) {
  if (!productInfo.value.dealerItemSkuDTOList) return false
  
  // 查找包含该规格值的SKU，检查是否有库存且状态可用
  return productInfo.value.dealerItemSkuDTOList.some((sku: any) => {
    return sku.spItemSkuSpecificationIds?.includes(`${specId}-${attrValueId}`) 
      && sku.state !== 0 
      && sku.inventory > 0
  })
}

// 更新当前显示的图片
function updateCurrentImage(imageUrl: string) {
  if (!imageUrl) return

  // 找到对应的图片索引
  const imageIndex = productImages.value.findIndex((img: any) => img.iconUrl === imageUrl)
  if (imageIndex !== -1) {
    slideIndex.value = imageIndex
  }
}

// 根据选中的规格匹配对应的SKU
function matchSelectedSku() {
  if (!productInfo.value.dealerItemSkuDTOList) return

  const selectedSkuId = findMatchingSku()
  selectedSku.value = null
  if (selectedSkuId) {
    selectedSku.value = productInfo.value.dealerItemSkuDTOList.find((sku: any) => sku.id === selectedSkuId)
  }
}

// 将指定 SKU 的规格同步到选项上
function applySkuSelection(sku: any) {
  if (!sku?.dealerItemSkuSpecReqDTOs) return

  const nextSelection: Record<string, string> = {}
  sku.dealerItemSkuSpecReqDTOs.forEach((spec: any) => {
    if (spec.dealerItemSkuSpecDTO) {
      nextSelection[spec.id] = spec.dealerItemSkuSpecDTO.id
    }
  })

  if (Object.keys(nextSelection).length > 0) {
    selectedAttributes.value = nextSelection
  }
  selectedSku.value = sku
}

// 查找匹配的SKU - 根据 spItemSkuSpecificationIds 匹配
function findMatchingSku() {
  if (!productInfo.value.dealerItemSkuDTOList) return null

  // 构建选中的规格ID字符串，格式：specGroupId-specValueId,
  const selectedSpecIds = Object.keys(selectedAttributes.value).map(specGroupId => {
    const specValueId = selectedAttributes.value[specGroupId]
    return `${specGroupId}-${specValueId}`
  }).sort().join(',') + ','

  // 在 SKU 列表中查找匹配的
  const matchedSku = productInfo.value.dealerItemSkuDTOList.find((sku: any) => {
    if (!sku.spItemSkuSpecificationIds) return false
    
    // 将 SKU 的规格 ID 也排序后比较
    const skuSpecIds = sku.spItemSkuSpecificationIds.split(',').filter((s: string) => s.trim()).sort().join(',') + ','
    return skuSpecIds === selectedSpecIds
  })

  return matchedSku?.id
}

// 初始化规格选择
function initializeSpecSelection() {
  if (!productInfo.value.dealerItemSkuDTOList) return
  if (!productInfo.value.dealerItemSkuSpecDTOs?.length) {
    selectedAttributes.value = {}
    selectedSku.value = productInfo.value.dealerItemSkuDTOList.find((sku: any) => sku.state !== 0 && Number(sku.inventory) > 0) || null
    return
  }

  // 重置选中的规格
  selectedAttributes.value = {}

  // 为每个规格组选择第一个有库存的规格值
  productInfo.value.dealerItemSkuSpecDTOs.forEach((specGroup: any) => {
    const specValues = specGroup.dealerItemSkuSpecReqDTOs || []
    
    // 如果只有一个规格值，直接选中（不判断库存）
    if (specValues.length === 1) {
      selectedAttributes.value[specGroup.id] = specValues[0].id
      return
    }
    
    // 如果有多个规格值，获取第一个有库存的规格值
    const firstAvailableSpecValue = specValues.find((specValue: any) => {
      // 检查是否有包含该规格值的可用SKU（state不为0且有库存）
      return productInfo.value.dealerItemSkuDTOList?.some((sku: any) => {
        return sku.spItemSkuSpecificationIds?.includes(`${specGroup.id}-${specValue.id}`) 
          && sku.state !== 0 
          && sku.inventory > 0
      })
    })

    if (firstAvailableSpecValue) {
      // specGroup.id 是规格组ID，firstAvailableSpecValue.id 是规格值ID
      selectedAttributes.value[specGroup.id] = firstAvailableSpecValue.id
    }
  })

  // 根据选中的规格匹配对应的SKU
  matchSelectedSku()

  // 如果匹配的SKU没有库存，尝试选择下一个有库存的规格组合
  if (!selectedSku.value || selectedSku.value.inventory <= 0 || selectedSku.value.state === 0) {
    findNextAvailableSku()
  }

  // 设置默认图片索引
  if (selectedSku.value) {
    const imageIndex = productImages.value.findIndex((img: any) => img.id === selectedSku.value.id)
    if (imageIndex !== -1) {
      slideIndex.value = imageIndex
    }
  }
}

// 查找下一个有库存的SKU组合
function findNextAvailableSku() {
  if (!productInfo.value.dealerItemSkuDTOList || !productInfo.value.dealerItemSkuSpecDTOs) return

  // 获取所有有库存的SKU
  const availableSkus = productInfo.value.dealerItemSkuDTOList.filter((sku: any) => {
    return sku.state !== 0 && sku.inventory > 0
  })

  if (availableSkus.length === 0) return

  // 按顺序尝试每个有库存的SKU
  for (const sku of availableSkus) {
    // 检查该SKU的规格组合是否完整
    if (sku.dealerItemSkuSpecReqDTOs && sku.dealerItemSkuSpecReqDTOs.length > 0) {
      // 应用该SKU的规格选择
      applySkuSelection(sku)
      
      // 验证当前选择的SKU是否有库存
      if (selectedSku.value && selectedSku.value.inventory > 0 && selectedSku.value.state !== 0) {
        return // 找到了有库存的SKU
      }
    }
  }
}

// 缩略图点击时同步 SKU 规格
function handleSkuImageClick(index: number) {
  // 如果所有SKU的iconUrl都为空，禁用切换
  if (allSkuIconUrlsEmpty.value) {
    return
  }

  slideIndex.value = index

  const clickedSkuId = productImages.value?.[index]?.id
  if (!clickedSkuId) return

  const clickedSku = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => sku.id === clickedSkuId)
  if (clickedSku) {
    applySkuSelection(clickedSku)
  }
}

// 当前基础价格
const currentPrice = computed(() => {
  if (selectedSku.value) {
    return selectedSku.value.finalPrice ?? selectedSku.value.price ?? selectedSku.value.skuPrice
  }
  return productInfo.value.finalPrice || productInfo.value.skuPrice || productInfo.value.price || getMinPrice(productInfo.value)
})

// 当前会员价
const currentMemberPrice = computed(() => {
  if (selectedSku.value && selectedSku.value.discountPrice) {
    return selectedSku.value.discountPrice
  }
  return productInfo.value.discountPrice
})

const stripHtml = (value: string = '') => String(value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()

const australiaSummaryText = computed(() => {
  const richText = stripHtml(productInfo.value.simpleIntroduction || '')
  if (richText) {
    return richText.slice(0, 220)
  }

  const brand = productInfo.value.brandInfo || productInfo.value.brand || productInfo.value.title || 'This product'
  const flavorSummary = productSpecs.value
    .map((spec: any) => spec.specName)
    .filter(Boolean)
    .join(', ')

  if (flavorSummary) {
    return `${brand} is available with ${flavorSummary}. Choose your preferred option and place the order using our existing checkout flow.`
  }

  return `${brand} is available for direct purchase using our existing product, cart, and checkout logic.`
})

const australiaSalePrice = computed(() => {
  if (showMemberPrice.value) {
    return Number(currentMemberPrice.value || 0)
  }
  return Number(currentPrice.value || 0)
})

const australiaComparePrice = computed(() => {
  const candidate = Number(
    selectedSku.value?.originalPrice
    || selectedSku.value?.marketPrice
    || selectedSku.value?.price
    || productInfo.value.originalPrice
    || productInfo.value.marketPrice
    || productInfo.value.price
    || 0
  )
  return candidate > australiaSalePrice.value ? candidate : 0
})

const australiaUnitPrice = computed(() => getAustraliaSkuDiscountUnitPrice({
  sku: selectedSku.value,
  specs: productSpecs.value,
  selected: selectedAttributes.value,
  price: australiaSalePrice.value,
  comparePrice: australiaComparePrice.value,
}))

const australiaAvailabilityText = computed(() => {
  if (!currentSkuAvailable.value) {
    return 'Currently sold out'
  }

  const inventory = Number(selectedSku.value?.inventory || productInfo.value.spuInventory || productInfo.value.onHandQty || 0)
  if (inventory > 0) {
    return `${inventory} in stock and ready to ship`
  }

  return 'Available now'
})

function handleAustraliaSpecChange(spec: any, attrValueId: string) {
  const attrValue = spec?.children?.find((item: any) => item.id === attrValueId)
  if (attrValue) {
    handleSelectSpecValue(spec, attrValue)
  }
}

function setActiveTab(tab: 'detail' | 'reviews') {
  activeTab.value = tab
}

const vipIconUrl = 'http://sztjyshop.oss-cn-guangzhou.aliyuncs.com/imgs/2026/02-04/upload_pic_1770171482456_vip.png'

const showMemberPrice = computed(() => {
  const basePrice = Number(currentPrice.value)
  const memberPrice = Number(currentMemberPrice.value)
  if (!memberPrice || !basePrice) return false
  return memberPrice !== basePrice
})

const nextLevelCouponInfo = ref<any>(null)
const nextLevelCouponLoading = ref(false)
const nextLevelCouponOrderAmount = computed(() => {
  const basePrice = Number(purchasePrice.value?.sale ?? australiaSalePrice.value ?? 0)
  const quantity = Math.max(1, Number(buyTotalQuantity.value || 1))
  return Number((basePrice * quantity).toFixed(2))
})

const formatNextLevelCouponMoney = (amount: number) =>
  formatMoney(Number(amount.toFixed(0)), undefined, { withCode: true })

const formatAustraliaDisplayMoney = (amount: number | string) =>
  formatMoney(Number(amount || 0), undefined, { withCode: true })

const isMallUserLoggedIn = () =>
  typeof window !== 'undefined' && !!window.localStorage.getItem(config.appTokenKey)

const shouldHideNextLevelCouponMessage = (message: any) => {
  const normalized = String(message || '').trim()
  if (!normalized) {
    return true
  }

  return [
    '暂无优惠券门槛信息',
    '暂无门槛券',
    '暂无门槛信息',
    '暂无优惠信息',
    '無優惠券門檻資訊',
    '無門檻券資訊',
    'クーポン条件情報はありません',
    '次のクーポン情報はありません'
  ].some((keyword) => normalized.includes(keyword))
}

const nextLevelCouponMessage = computed(() => {
  const data = nextLevelCouponInfo.value || {}
  const directMessage = data.discountDesc || data.desc || data.message || data.tip
  if (directMessage && !shouldHideNextLevelCouponMessage(directMessage)) {
    return directMessage
  }

  const differenceAmount = Number(
    data.differenceAmount ?? data.diffAmount ?? data.needAmount ?? data.remainAmount ?? 0
  )
  const nextConditionValue = Number(
    data.nextConditionValue ?? data.nextLevelConditionValue ?? data.conditionValue ?? data.nextAmount ?? 0
  )
  const discountAmount = Number(
    data.discountAmount ?? data.faceValue ?? data.reduceAmount ?? data.couponAmount ?? 0
  )

  if (differenceAmount > 0 && discountAmount > 0) {
    return `Spend ${formatNextLevelCouponMoney(differenceAmount)} more to save ${formatNextLevelCouponMoney(discountAmount)} with your next coupon.`
  }

  if (differenceAmount > 0 && nextConditionValue > 0) {
    return `Spend ${formatNextLevelCouponMoney(differenceAmount)} more to unlock the next coupon at ${formatNextLevelCouponMoney(nextConditionValue)}.`
  }

  return ''
})

async function loadNextLevelCouponInfo() {
  if (!productInfo.value?.id || nextLevelCouponOrderAmount.value <= 0 || !isMallUserLoggedIn()) {
    nextLevelCouponInfo.value = null
    return
  }

  nextLevelCouponLoading.value = true
  try {
    const response = await queryNextLevelCoupon({
      param: {
        orderAmount: nextLevelCouponOrderAmount.value,
      }
    })
    nextLevelCouponInfo.value = response?.data?.data || response?.data || null
  } catch (error) {
    nextLevelCouponInfo.value = null
  } finally {
    nextLevelCouponLoading.value = false
  }
}

// 当前SKU是否可用
const currentSkuAvailable = computed(() => {
  if (selectedSku.value) {
    return selectedSku.value.state !== 0 && selectedSku.value.inventory > 0
  }
  return false
})

const productSpecs = computed(() => {
  if (!productInfo.value.dealerItemSkuSpecDTOs) return []

  // 从 dealerItemSkuSpecDTOs 构建规格选项
  return productInfo.value.dealerItemSkuSpecDTOs.map((specGroup: any) => {
    return {
      id: specGroup.id,
      specName: specGroup.specName,
      children: (specGroup.dealerItemSkuSpecReqDTOs || [])
        .filter((specValue: any) => {
          // 过滤掉没有可用SKU的规格值（state为0的SKU）
          const hasAvailableSku = productInfo.value.dealerItemSkuDTOList?.some((sku: any) => {
            return sku.spItemSkuSpecificationIds?.includes(`${specGroup.id}-${specValue.id}`) && sku.state !== 0
          })
          return hasAvailableSku
        })
        .map((specValue: any) => {
          // 查找包含该规格值的可用SKU（state不为0），用于获取图片
          const matchingSku = productInfo.value.dealerItemSkuDTOList?.find((sku: any) => {
            return sku.spItemSkuSpecificationIds?.includes(`${specGroup.id}-${specValue.id}`) && sku.state !== 0
          })
          
          return {
            id: specValue.id,
            specName: specValue.specName,
            skuImage: matchingSku?.iconUrl || ''
          }
        })
    }
  })
})



function getMinPrice(item: any) {
  if (!item.dealerItemSkuDTOList) return 0;
  const priceNumArrs = item.dealerItemSkuDTOList
    ? item.dealerItemSkuDTOList.map((o: any) => o.price)
    : [];
  
  // 检查数组是否为空
  if (priceNumArrs.length === 0) return 0;
  
  const minPrice = priceNumArrs.reduce((prev: number, next: number) => {
    return Math.min(prev, next);
  });
  return minPrice || 0;
}





const handleChange = () => {
  // 处理数量变化
}

const handleReviewSubmitted = (review: any) => {
  // 处理新评论逻辑
  console.log('新评论提交:', review)
  // 可以在这里更新评论数量等
  if (reviewsRef.value) {
    // 刷新评论数据
    reviewsRef.value.refreshComments()
  }
}

// 获取评价数据的方法
const getReviewData = async () => {
  if (reviewsRef.value) {
    try {
      // 从澳洲评论组件获取评价数据
      const reviews = reviewsRef.value.getReviews()
      const avgRating = reviewsRef.value.getAverageRating()
      
      // 更新本地数据
      totalReviews.value = reviews.length
      averageRating.value = parseFloat(avgRating) || 0
      ratingValue.value = averageRating.value
      commentNum.value = totalReviews.value
      
      console.log('评价数据:', {
        totalReviews: totalReviews.value,
        averageRating: averageRating.value,
        reviews: reviews
      })
    } catch (error) {
      console.error('获取评价数据失败:', error)
    }
  }
}

// 监听评论组件数据变化
const updateReviewData = () => {
  if (reviewsRef.value) {
    const reviews = reviewsRef.value.getReviews()
    const avgRating = reviewsRef.value.getAverageRating()
    
    totalReviews.value = reviews.length
    averageRating.value = parseFloat(avgRating) || 0
    ratingValue.value = averageRating.value
    commentNum.value = totalReviews.value
  }
}

// 图片导航函数
function previousImage() {
  // 如果所有SKU的iconUrl都为空，禁用切换
  if (allSkuIconUrlsEmpty.value) {
    return
  }
  if (slideIndex.value > 0) {
    handleSkuImageClick(slideIndex.value - 1)
  }
}

function nextImage() {
  // 如果所有SKU的iconUrl都为空，禁用切换
  if (allSkuIconUrlsEmpty.value) {
    return
  }
  if (slideIndex.value < productImages.value.length - 1) {
    handleSkuImageClick(slideIndex.value + 1)
  }
}

// 缩略图滚动函数
function scrollThumbsLeft() {
  if (thumbsRef.value) {
    // 计算每个缩略图的宽度（包括gap）
    const thumbWidth = thumbsRef.value.scrollWidth / productImages.value.length
    const scrollAmount = thumbWidth * 4 // 每次滚动4个缩略图的宽度
    thumbsRef.value.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    })
  }
}

function scrollThumbsRight() {
  if (thumbsRef.value) {
    // 计算每个缩略图的宽度（包括gap）
    const thumbWidth = thumbsRef.value.scrollWidth / productImages.value.length
    const scrollAmount = thumbWidth * 4 // 每次滚动4个缩略图的宽度
    thumbsRef.value.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    })
  }
}

// 判断是否需要显示滚动按钮（超过4张时）
const showThumbScrollButtons = computed(() => {
  return !isSmallScreen.value && productImages.value.length > 4
})

const stickyBarImageUrl = computed(() => currentImageUrl.value || replaceCdnDomain(productInfo.value.iconUrl || ''))

onMounted(async () => {
  if (!props.ssrProductDetail) await getDetail()

  // Analytics 埋点：查看商品
  if (productInfo.value) {

    let val= productInfo.value.price|| 0;
    if(!val && productInfo.value?.dealerItemSkuDTOList&&productInfo.value?.dealerItemSkuDTOList.length>0) {
      val = productInfo.value?.dealerItemSkuDTOList[0]?.price|| 0;
    }
    trackEvent('ViewContent', {
      contents: [{
        content_id: productInfo.value.id,
        content_name: productInfo.value.title || productInfo.value.subject,
        content_type: 'product'
      }],
      value:val,
      currency: baseCurrencyCode.value,
      // 保留Google Analytics向后兼容性
      category: 'Product',
      action: 'view_product',
      label: productInfo.value.title || productInfo.value.subject
    })
  }

  // 延迟获取评价数据，确保评论组件已经加载完成
  setTimeout(() => {
    getReviewData()
  }, 1000)

  // 设置定期更新评价数据
  reviewUpdateInterval = setInterval(() => {
    updateReviewData()
  }, 3000) // 每3秒更新一次

})

const productImages = computed(() => getAustraliaProductImages(productInfo.value))

// react to prop updates when navigating client-side
watch(() => props.ssrProductDetail, (val) => {
  if (val) {
    try {
      productInfo.value = normalizeAustraliaPurchaseDetail(val)
      const initialCombo = productInfo.value.hasCombo === true && productInfo.value.comboList?.find((offer: any) => String(offer.comboCode) === String(props.initialComboCode || ''))
      purchasePrice.value = initialCombo ? { sale: Number(initialCombo.salePrice), compare: 0 } : null
      initializeSpecSelection()
      productError.value = false
    } catch { productError.value = true }
  }
}, { immediate: true })

watch(
  () => [productInfo.value?.id, nextLevelCouponOrderAmount.value],
  async ([productId]) => {
    if (!productId) {
      nextLevelCouponInfo.value = null
      return
    }
    await loadNextLevelCouponInfo()
  },
  { immediate: true }
)

// 监听detailId变化，重新获取评价数据
watch(detailId, (newId) => {
  if (newId) {
    setTimeout(() => {
      getReviewData()
    }, 1500)
  }
})

// 检查所有SKU的iconUrl是否都为空
const allSkuIconUrlsEmpty = computed(() => {
  if (!productInfo.value.dealerItemSkuDTOList || productInfo.value.dealerItemSkuDTOList.length === 0) {
    return false
  }
  // 检查所有SKU的iconUrl是否都为空或无效
  return productInfo.value.dealerItemSkuDTOList.every((sku: any) => {
    const iconUrl = sku.iconUrl && sku.iconUrl.trim()
    return !iconUrl
  })
})


// 定期更新评价数据
let reviewUpdateInterval: NodeJS.Timeout | null = null

// 组件卸载时清理定时器
onUnmounted(() => {
  if (reviewUpdateInterval) {
    clearInterval(reviewUpdateInterval)
  }
})

// 暴露方法给父组件
defineExpose({
  getReviewData,
  updateReviewData,
  getReviewStats: () => ({
    totalReviews: totalReviews.value,
    averageRating: averageRating.value,
    commentNum: commentNum.value
  })
})

const productImagePreviewList = computed(() => {
  return productImages.value
    .filter((item: any) => !!item.iconUrl)
    .map((item: any) => replaceCdnDomain(item.iconUrl))
})

// 当前主图片 URL（应用 CDN 域名替换）
const currentImageUrl = computed(() => {
  const currentImage = productImages.value[slideIndex.value]
  if (!currentImage?.iconUrl) return ''
  return replaceCdnDomain(currentImage.iconUrl)
})

// 获取缩略图 URL（应用 CDN 域名替换和 transferOssUrl）
const getThumbImageUrl = (iconUrl: string) => {
  if (!iconUrl) return ''
  const processedUrl = transferOssUrl(iconUrl, 300)
  return replaceCdnDomain(processedUrl)
}
// 构建规格信息字符串
function buildSpecsInfo() {
  const specs: string[] = []
  
  // 如果没有选中任何规格，返回空字符串
  if (!selectedAttributes.value || Object.keys(selectedAttributes.value).length === 0) {
    return ''
  }
  
  Object.keys(selectedAttributes.value).forEach(specGroupId => {
    const specValueId = selectedAttributes.value[specGroupId]
    
    // 从 dealerItemSkuSpecDTOs 查找规格组
    const specGroup = productInfo.value.dealerItemSkuSpecDTOs?.find((s: any) => s.id === specGroupId)
    
    if (specGroup) {
      // 在规格组的子项中查找规格值
      const specValue = specGroup.dealerItemSkuSpecReqDTOs?.find((v: any) => v.id === specValueId)
      
      if (specValue) {
        specs.push(`${specGroup.specName}:${specValue.specName}`)
      }
    }
  })
  
  return specs.join(',')
}

</script>

<template>
  <main class="au-pdp" :class="{ 'au-pdp--modal': isModal }">
    <nav v-if="!isModal" class="au-pdp__breadcrumb" aria-label="Breadcrumb"><NuxtLink to="/">Home</NuxtLink><span>/</span><NuxtLink to="/product">Products</NuxtLink><span>/</span><span>{{ productInfo.title || 'Product' }}</span></nav>
    <div v-if="productError" class="au-pdp__error" role="alert"><h1>Unable to load this product</h1><button type="button" @click="getDetail(false)">Try again</button></div>
    <section v-else ref="productCardRef" class="au-pdp__main">
      <MistrivoProductGallery v-model="slideIndex" :images="productImages.map(image => ({ id: image.id, src: replaceCdnDomain(image.iconUrl) }))" :title="productInfo.title" />
      <div class="au-pdp__summary">
        <header ref="productHeadingRef" class="au-pdp__heading" tabindex="-1"><h1>{{ productInfo.title }}</h1><button type="button" class="au-pdp__rating" @click="scrollToReviews"><el-rate :model-value="Number(averageRating || 0)" disabled /><span>({{ totalReviews || 0 }} Reviews)</span></button><div class="au-pdp__price"><strong>{{ formatAustraliaDisplayMoney(purchasePrice?.sale ?? australiaSalePrice) }}</strong><del v-if="(purchasePrice?.compare ?? australiaComparePrice) > 0">{{ formatAustraliaDisplayMoney(purchasePrice?.compare ?? australiaComparePrice) }}</del></div></header>
        <MistrivoSpuPurchaseOptions v-model="buyTotalQuantity" :detail="productInfo" :selected-sku="selectedSku" :specs="productSpecs" :selected="selectedAttributes" :available="isSpecValueAvailable" :option-price="getSpecOptionPrice" :option-discount="getSpecOptionDiscount" :unit-price="australiaUnitPrice" :format-money="formatAustraliaDisplayMoney" :single-price="australiaSalePrice" :currency="baseCurrencyCode" :initial-combo-code="initialComboCode || ''" :is-modal="isModal" @select="handleSelectSpecValue" @price-change="purchasePrice = $event" @add-to-cart-success="emit('addToCartSuccess')" @refresh="getDetail(true)" />
        <p v-if="nextLevelCouponMessage" class="au-pdp__coupon">{{ nextLevelCouponMessage }}</p>
        <MistrivoPaymentMarks />
        <section v-if="!isModal" class="au-pdp__description"><h2>Description</h2><div v-if="productInfo.simpleIntroduction" class="au-pdp__rich-text" v-html="productInfo.simpleIntroduction"></div><p v-else>{{ productInfo.title }}</p></section>
      </div>
    </section>
    <ClientOnly v-if="!isModal && !productError"><MistrivoProductInformation :target="productHeadingRef" :title="productInfo.title" :image="stickyBarImageUrl" :description="buildSpecsInfo()" /></ClientOnly>
    <template v-if="!isModal && !productError"><MistrivoRecommendations :exclude-id="productInfo.id" /><section ref="reviewSection" class="au-pdp__reviews"><RateItem :product-id="productInfo.dealerShopId" :productInfo-spu-id="productInfo.id" :product-comment-id="productInfo.dealerInfoId" :product-name="productInfo.title" :product-brand="productInfo.brand" :product-image="currentImageUrl" @review-submitted="handleReviewSubmitted" ref="reviewsRef" /></section><MistrivoServiceBenefits /></template>
  </main>
</template>

<style src="~/templates/australia/styles/product-detail.css" scoped></style>
